import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Dialog from '@mui/material/Dialog';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { PillIcon } from 'shared/ui/soft/PillIcon';

/* ---------------- utils ---------------- */
function pad2(n) {
  return String(n).padStart(2, '0');
}
function hm(min) {
  const m = Number(min) || 0;
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}
function trDateLabel(iso) {
  try {
    const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
    const date = d.toLocaleDateString('tr-TR');
    const day = d.toLocaleDateString('tr-TR', { weekday: 'long' });
    const Day = day ? day.charAt(0).toUpperCase() + day.slice(1) : '';
    return `${date} • ${Day}`;
  } catch {
    return String(iso || '');
  }
}
function moneyTR(amt) {
  const n = Number(amt || 0);
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(n);
  } catch {
    return `${n} TL`;
  }
}

/* ---------------- normalize layer ---------------- */
function normalizeSession(raw) {
  const s = raw || {};

  const id = s?.id ?? s?.sessionId ?? s?.session_id ?? s?.sid ?? '';
  const dayISO = String(s?.dayISO ?? s?.day ?? s?.date ?? s?.sessionDay ?? s?.dayIso ?? '').slice(
    0,
    10
  );

  const startMin =
    Number(s?.startMin ?? s?.start ?? s?.startMinute ?? s?.start_min ?? s?.startAtMin ?? 0) || 0;

  const trainerId =
    s?.trainerId ?? s?.trainer?.id ?? s?.trainer_id ?? s?.instructorId ?? s?.coachId ?? null;

  const trainerName =
    s?.trainerName ??
    s?.trainerNameSnapshot ??
    s?.trainer?.name ??
    s?.trainerFullName ??
    s?.coachName ??
    '—';

  const activityName =
    s?.activityName ??
    s?.activityNameSnapshot ??
    s?.packageItemActivityName ??
    s?.activity?.name ??
    s?.title ??
    'Seans';

  const capacity = Number(s?.capacity ?? s?.cap ?? s?.classCapacity ?? 0) || 0;

  const participantsRaw = Array.isArray(s?.participants)
    ? s.participants
    : Array.isArray(s?.members)
    ? s.members.map((name, idx) => ({ id: idx + 1, fullName: String(name) }))
    : [];

  const participants = participantsRaw.map((p) => ({
    memberId: p?.memberId ?? p?.id ?? null,
    fullName: p?.fullName ?? p?.memberNameSnapshot ?? p?.memberName ?? p?.name ?? '',
    sessionDebtTry: Number(p?.sessionDebtTry ?? 0) || 0,
    memberDebtTry: Number(p?.memberDebtTry ?? 0) || 0,
    paymentStatus: p?.paymentStatus ?? p?.payment_state ?? null,
    note: p?.note ?? p?.attendanceNote ?? p?.memberNote ?? null,
  }));

  const participantCount =
    Number(s?.participantCount ?? s?.booked ?? s?.bookedCount ?? participants.length ?? 0) || 0;

  const note = s?.note ?? s?.sessionNote ?? s?.notes ?? s?.detailNote ?? null;
  const flags = Array.isArray(s?.flags) ? s.flags : [];
  const debtAmount = Number(s?.debtAmount ?? s?.debtTryTotal ?? 0) || 0;

  return {
    id: String(id),
    dayISO,
    startMin,
    trainerId: trainerId != null ? String(trainerId) : null,
    trainerName,
    activityName,
    capacity,
    participantCount,
    participants,
    note,
    flags,
    debtAmount,
  };
}

/* ---------------- helpers ---------------- */
function sid(s) {
  return String(s?.id ?? '');
}
function sDayISO(s) {
  return String(s?.dayISO || '').slice(0, 10);
}
function sCap(s) {
  return Number(s?.capacity ?? 0);
}
function sBooked(s) {
  return Number(
    s?.participantCount ?? (Array.isArray(s?.participants) ? s.participants.length : 0)
  );
}
function sActivity(s) {
  return s?.activityName || 'Seans';
}
function sParticipants(s) {
  return Array.isArray(s?.participants) ? s.participants : [];
}

function compactMemberNames(s, limit = 28) {
  const names = sParticipants(s)
    .map((p) => String(p?.fullName || '').trim())
    .filter(Boolean);

  if (!names.length) return '—';

  if (names.length === 1) {
    const n = names[0];
    return n.length > limit ? `${n.slice(0, limit)}…` : n;
  }

  const first = names[0];
  const base = first.length > limit ? `${first.slice(0, limit)}…` : first;
  return `${base} +${names.length - 1}`;
}

/* note: önce session note, yoksa participant note'lar */
function sNoteText(s) {
  const base = String(s?.note ?? '').trim();
  if (base) return base;

  const notes = sParticipants(s)
    .map((p) => String(p?.note ?? '').trim())
    .filter(Boolean);

  if (!notes.length) return '';
  return Array.from(new Set(notes)).join('\n');
}
function hasNote(s) {
  return Boolean(sNoteText(s));
}

function participantsWithDebt(s) {
  const ps = sParticipants(s);
  const debtors = ps.filter((p) => Number(p?.memberDebtTry || 0) > 0);

  const map = new Map();
  debtors.forEach((p) => {
    const k = String(p?.memberId ?? p?.fullName ?? '');
    if (!k) return;
    if (!map.has(k)) map.set(k, p);
  });
  return Array.from(map.values());
}
function sessionDebtAmount(s) {
  return sParticipants(s).reduce((acc, p) => acc + Number(p?.memberDebtTry || 0), 0);
}
function hasDebt(s) {
  return participantsWithDebt(s).length > 0;
}
function hasDetails(s) {
  const hasFlags = Array.isArray(s?.flags) && s.flags.length > 0;
  return hasNote(s) || hasDebt(s) || hasFlags;
}

function groupByStartMin(list) {
  const map = new Map();
  for (const s of list) {
    const k = Number(s?.startMin ?? 0);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(s);
  }
  const keys = Array.from(map.keys()).sort((a, b) => a - b);
  return keys.map((k) => ({ startMin: k, items: map.get(k) }));
}

/* ---------------- shared "ProgramPanel DNA" styles ---------------- */
function cardShell(theme, { radius = '16px' } = {}) {
  const isDark = theme.palette.mode === 'dark';
  return {
    borderRadius: radius,
    border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.12)}`,
    bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.9),
  };
}

function iconBox(
  theme,
  color,
  { size = 30, radius = '12px', darkAlpha = 0.16, lightAlpha = 0.1 } = {}
) {
  const isDark = theme.palette.mode === 'dark';
  return {
    width: size,
    height: size,
    borderRadius: radius,
    display: 'grid',
    placeItems: 'center',
    bgcolor: alpha(color, isDark ? darkAlpha : lightAlpha),
    border: `1px solid ${alpha(color, 0.22)}`,
  };
}

/* ---------------- UI: time group header (ProgramPanel chip-like) ---------------- */
function TimeGroupHeader({ label }) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <MDBox
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 0.6,
      }}
    >
      <Chip
        size="small"
        label={label}
        variant="outlined"
        sx={{
          borderRadius: '999px',
          fontWeight: 900,
          height: 24,
          bgcolor: isDark ? alpha('#fff', 0.02) : alpha('#fff', 0.7),
          '& .MuiChip-label': { px: 1.0, fontSize: 11.5, fontWeight: 900 },
        }}
      />
    </MDBox>
  );
}
TimeGroupHeader.propTypes = { label: PropTypes.string.isRequired };

/* ---------------- UI: row (ProgramPanel row + icons DNA) ---------------- */
function TodaySessionListRow({ s, open, onToggle }) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canExpand = hasDetails(s);
  const noteExists = hasNote(s);
  const debtPeople = participantsWithDebt(s);
  const debtExists = debtPeople.length > 0;

  const isLast = Array.isArray(s?.flags) && s.flags.includes('LAST_SESSION');

  const cap = sCap(s);
  const booked = sBooked(s);
  const capText = cap ? `${booked}/${cap}` : `${booked}`;

  const nameText = compactMemberNames(s, isMobile ? 22 : 28);
  const allNames = sParticipants(s)
    .map((p) => p?.fullName)
    .filter(Boolean)
    .join(', ');

  return (
    <MDBox>
      {/* Row (ProgramPanel style) */}
      <MDBox
        onClick={canExpand ? onToggle : undefined}
        sx={{
          p: 0.75,
          borderRadius: '14px',
          border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.12 : 0.1)}`,
          bgcolor: isDark ? alpha('#0b1220', 0.14) : alpha('#0b1220', 0.02),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          cursor: canExpand ? 'pointer' : 'default',
          transition: 'background .12s ease, border-color .12s ease',
          '&:hover': canExpand
            ? {
                bgcolor: isDark ? alpha('#0b1220', 0.22) : alpha('#0b1220', 0.04),
                borderColor: alpha(theme.palette.info.main, 0.22),
              }
            : undefined,
          '&:active': canExpand ? { transform: 'scale(0.997)' } : undefined,
        }}
      >
        {/* Left */}
        <MDBox sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Üst satır */}
          <MDTypography variant="button" sx={{ fontWeight: 900, lineHeight: 1.15 }} noWrap>
            {hm(s?.startMin)}{' '}
            <MDTypography
              component="span"
              variant="caption"
              sx={{ opacity: 0.75, fontWeight: 900 }}
            >
              {sActivity(s)}
            </MDTypography>
            {/* Eğitmen */}
            <MDTypography
              component="span"
              variant="caption"
              sx={{
                ml: 0.8,
                fontWeight: 900,
                color:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.success.main, 0.95)
                    : theme.palette.success.dark,
                whiteSpace: 'nowrap',
              }}
            >
              • {s?.trainerName || '—'}
            </MDTypography>
          </MDTypography>

          {/* Katılımcılar (alt satır) */}
          <Tooltip
            title={allNames || 'Katılımcı yok'}
            arrow
            placement="top"
            disableHoverListener={allNames.length < 40}
          >
            <MDTypography
              variant="caption"
              sx={{
                opacity: 0.82,
                fontWeight: 800,

                // ✅ GERÇEK boşluk burada
                pt: 0.6, // ⬅ mt yerine pt
                lineHeight: 1.25,

                maxWidth: '100%',
              }}
              noWrap
            >
              {allNames || 'Katılımcı yok'}
            </MDTypography>
          </Tooltip>
        </MDBox>

        {/* Right icons (ProgramPanel icon-box DNA) */}
        <MDBox
          sx={{ display: 'flex', alignItems: 'center', gap: 0.55, flex: '0 0 auto' }}
          onClick={(e) => e.stopPropagation()}
        >
          {debtExists ? (
            <Tooltip
              title={
                <span style={{ whiteSpace: 'pre-line' }}>
                  Borç var:
                  {'\n'}
                  {debtPeople
                    .map((p) => {
                      const n = p?.fullName || '—';
                      const amt = Number(p?.memberDebtTry || 0);
                      return `• ${n}: ${moneyTR(amt)}`;
                    })
                    .join('\n')}
                </span>
              }
              arrow
              placement="top"
            >
              <MDBox
                sx={iconBox(theme, theme.palette.error.main, {
                  size: 30,
                  radius: '12px',
                  darkAlpha: 0.18,
                  lightAlpha: 0.12,
                })}
              >
                <Icon sx={{ fontSize: 18, color: theme.palette.error.main }}>payments</Icon>
              </MDBox>
            </Tooltip>
          ) : (
            <MDBox sx={{ width: 30, height: 30 }} />
          )}

          {noteExists ? (
            <Tooltip title={sNoteText(s)} arrow placement="top">
              <MDBox
                sx={iconBox(theme, theme.palette.warning.main, {
                  size: 30,
                  radius: '12px',
                  darkAlpha: 0.16,
                  lightAlpha: 0.12,
                })}
              >
                <Icon sx={{ fontSize: 18, color: theme.palette.warning.main }}>error_outline</Icon>
              </MDBox>
            </Tooltip>
          ) : (
            <MDBox sx={{ width: 30, height: 30 }} />
          )}

          {canExpand ? (
            <IconButton
              size="small"
              onClick={onToggle}
              sx={{
                width: 30,
                height: 30,
                borderRadius: '12px',
                border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.12 : 0.1)}`,
              }}
            >
              <Icon sx={{ fontSize: 18, opacity: 0.85 }}>
                {open ? 'expand_less' : 'expand_more'}
              </Icon>
            </IconButton>
          ) : null}
        </MDBox>
      </MDBox>

      {/* Details (ProgramPanel dashed wrapper DNA) */}
      <Collapse in={open} timeout={160} unmountOnExit>
        <MDBox
          sx={{
            mt: 0.55,
            p: 0.75,
            borderRadius: '14px',
            border: `1px dashed ${alpha(theme.palette.text.primary, isDark ? 0.16 : 0.14)}`,
            bgcolor: isDark ? alpha('#0b1220', 0.12) : alpha('#0b1220', 0.015),
            display: 'grid',
            gap: 0.7,
          }}
        >
          {/* debt box */}
          {debtExists ? (
            <MDBox
              sx={{
                p: 1.0,
                borderRadius: '16px',
                bgcolor: alpha(theme.palette.error.main, isDark ? 0.12 : 0.07),
                display: 'grid',
                gap: 0.55,
              }}
            >
              <MDBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Icon sx={{ color: theme.palette.error.main }} fontSize="small">
                    payments
                  </Icon>
                  <MDTypography variant="button" fontWeight="bold" sx={{ fontSize: 12.5 }}>
                    Borç Detayı
                  </MDTypography>
                </MDBox>

                {sessionDebtAmount(s) > 0 ? (
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{ color: theme.palette.error.main, fontSize: 12.5 }}
                  >
                    {moneyTR(sessionDebtAmount(s))}
                  </MDTypography>
                ) : null}
              </MDBox>

              <MDTypography variant="caption" sx={{ opacity: 0.92, lineHeight: 1.45 }}>
                {debtPeople
                  .map((p) => {
                    const n = p?.fullName || '—';
                    const amt = Number(p?.memberDebtTry || 0);
                    return amt > 0 ? `${n} (${moneyTR(amt)})` : n;
                  })
                  .join(', ')}
              </MDTypography>
            </MDBox>
          ) : (
            <MDTypography variant="caption" sx={{ fontWeight: 900, opacity: 0.7 }}>
              Borç: yok
            </MDTypography>
          )}

          {/* note box */}
          {noteExists ? (
            <MDBox
              sx={{
                p: 1.0,
                borderRadius: '16px',
                bgcolor: alpha(theme.palette.warning.main, isDark ? 0.13 : 0.08),
              }}
            >
              <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.4 }}>
                <Icon sx={{ color: theme.palette.warning.main }} fontSize="small">
                  priority_high
                </Icon>
                <MDTypography variant="button" fontWeight="bold" sx={{ fontSize: 12.5 }}>
                  Not
                </MDTypography>
              </MDBox>

              <MDTypography
                variant="caption"
                sx={{ opacity: 0.92, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}
              >
                {sNoteText(s)}
              </MDTypography>
            </MDBox>
          ) : (
            <MDTypography variant="caption" sx={{ fontWeight: 900, opacity: 0.7 }}>
              Not: yok
            </MDTypography>
          )}
        </MDBox>
      </Collapse>
    </MDBox>
  );
}

TodaySessionListRow.propTypes = {
  s: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

/* ---------------- modal ---------------- */
export default function TodaySessionsModal({ open, onClose, todayISO, sessions, trainers }) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const normalized = useMemo(() => {
    const arr = Array.isArray(sessions) ? sessions : [];
    return arr.map(normalizeSession).filter((x) => x?.id);
  }, [sessions]);

  const data = normalized;

  const trainerOptionsRaw = useMemo(() => {
    if (Array.isArray(trainers) && trainers.length) return trainers;

    const map = new Map();
    data.forEach((s) => {
      const id = s?.trainerId;
      const name = s?.trainerName;
      if (id != null && !map.has(String(id))) {
        map.set(String(id), { id: String(id), name: name || `Eğitmen #${id}` });
      }
    });

    const arr = Array.from(map.values());
    return arr.length ? [{ id: 'ALL', name: 'Tümü' }, ...arr] : [{ id: 'ALL', name: 'Tümü' }];
  }, [trainers, data]);

  const [trainerId, setTrainerId] = useState('ALL');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (open) {
      setTrainerId('ALL');
      setExpandedId(null);
    }
  }, [open]);

  const today = String(todayISO || '').slice(0, 10);

  const trainerOptions = useMemo(() => {
    const base = trainerOptionsRaw.some((t) => String(t.id) === 'ALL')
      ? trainerOptionsRaw
      : [{ id: 'ALL', name: 'Tümü' }, ...trainerOptionsRaw];

    return base.map((t) => ({
      id: String(t?.id),
      name: t?.name || t?.fullName || t?.trainerName || String(t?.id),
    }));
  }, [trainerOptionsRaw]);

  const selectedTrainer = useMemo(() => {
    return trainerOptions.find((t) => t.id === String(trainerId)) || trainerOptions[0];
  }, [trainerId, trainerOptions]);

  const todaySessions = useMemo(() => {
    return data
      .filter((s) => sDayISO(s) === today)
      .filter((s) => (trainerId === 'ALL' ? true : String(s?.trainerId) === String(trainerId)))
      .sort(
        (a, b) =>
          Number(a?.startMin ?? 0) - Number(b?.startMin ?? 0) || sid(a).localeCompare(sid(b))
      );
  }, [data, today, trainerId]);

  const groups = useMemo(() => groupByStartMin(todaySessions), [todaySessions]);

  const chips = useMemo(() => {
    const total = todaySessions.length;
    const debtCount = todaySessions.filter((s) => hasDebt(s)).length;
    const noteCount = todaySessions.filter((s) => hasNote(s)).length;

    return [
      { label: `${total} seans` }, // ProgramPanel gibi
      noteCount ? { label: `Not ${noteCount}` } : null,
      debtCount ? { label: `Borç ${debtCount}` } : null,
    ].filter(Boolean);
  }, [todaySessions]);

  const modalBg =
    theme.palette.mode === 'dark'
      ? `radial-gradient(800px 380px at 12% -10%,
          ${alpha(theme.palette.warning.main, 0.16)}, transparent 55%),
        radial-gradient(700px 420px at 88% 0%,
          ${alpha(theme.palette.primary.main, 0.14)}, transparent 55%),
        ${theme.palette.background.paper}`
      : `radial-gradient(800px 380px at 12% -10%,
          ${alpha(theme.palette.warning.main, 0.12)}, transparent 55%),
        radial-gradient(700px 420px at 88% 0%,
          ${alpha(theme.palette.primary.main, 0.1)}, transparent 55%),
        ${theme.palette.background.paper}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          height: isMobile ? '100svh' : 'auto',
          maxHeight: isMobile ? '100svh' : '88vh',
          background: modalBg,
          border: 'none',
          borderRadius: isMobile ? 0 : '22px',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 18px 60px rgba(0,0,0,0.45)'
              : '0 18px 60px rgba(15,23,42,0.14)',
          overflow: 'hidden',
          touchAction: 'pan-y',
        },
      }}
    >
      {/* Header (ProgramPanel DNA) */}
      <MDBox
        sx={{
          px: isMobile ? 1.0 : 1.2,
          pt: isMobile ? 'calc(env(safe-area-inset-top) + 12px)' : 1.2,
          pb: 1.0,
          background: isDark
            ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.86)}, ${alpha(
                theme.palette.background.paper,
                0.52
              )})`
            : `linear-gradient(180deg, ${alpha('#fff', 0.92)}, ${alpha('#fff', 0.7)})`,
          backdropFilter: 'blur(14px)',
        }}
      >
        <MDBox
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          {/* Left: icon + titles */}
          <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
            <PillIcon icon="calendar_month" tone="teal" />
            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }} noWrap>
                Günlük Seanslar
              </MDTypography>
              <MDTypography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 800 }}
                noWrap
              >
                {trDateLabel(today)} • Seanslar • katılımcılar • uyarılar
              </MDTypography>
            </MDBox>
          </MDBox>

          {/* Right: count chip + filter + close */}
          <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
            <Chip
              size="small"
              label={`${todaySessions.length} seans`}
              variant="outlined"
              sx={{ borderRadius: '999px', fontWeight: 900 }}
            />

            <Autocomplete
              size="small"
              disableClearable
              options={trainerOptions}
              value={selectedTrainer}
              onChange={(_, v) => setTrainerId(String(v?.id || 'ALL'))}
              sx={{
                width: isMobile ? 160 : 220,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  minHeight: 40,
                  background: alpha(theme.palette.background.paper, isDark ? 0.35 : 0.75),
                },
                '& fieldset': { border: 'none' },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Eğitmen"
                  inputProps={{ ...params.inputProps, style: { fontSize: 13 } }}
                />
              )}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
            />

            <IconButton
              onClick={onClose}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '16px',
                bgcolor: alpha(theme.palette.error.main, 0.1),
                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.14) },
              }}
            >
              <Icon sx={{ color: theme.palette.error.main }}>close</Icon>
            </IconButton>
          </MDBox>
        </MDBox>

        {/* Summary chips (keep, but ProgramPanel weight) */}
        <MDBox
          sx={{
            mt: 0.9,
            display: 'flex',
            gap: 0.6,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': { height: 0 },
          }}
        >
          {chips.map((c) => (
            <Chip
              key={c.label}
              size="small"
              label={c.label}
              sx={{
                height: 24,
                borderRadius: '999px',
                border: 'none',
                bgcolor: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.06),
                '& .MuiChip-label': { px: 1.0, fontSize: 11.5, fontWeight: 900 },
              }}
            />
          ))}
        </MDBox>
      </MDBox>

      {/* Body */}
      <MDBox
        sx={{
          px: isMobile ? 1.0 : 1.2,
          py: 1.0,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          display: 'grid',
          gap: 1.0,
          pb: isMobile ? 10 : 6,
        }}
      >
        {!todaySessions.length ? (
          <MDBox
            sx={{
              ...cardShell(theme, { radius: '16px' }),
              p: 1.1,
            }}
          >
            <MDTypography variant="button" sx={{ opacity: 0.7, fontWeight: 800 }}>
              Bugün için seans yok.
            </MDTypography>
          </MDBox>
        ) : null}

        {/* Main list container (ProgramPanel day card DNA) */}
        <MDBox
          sx={{
            ...cardShell(theme, { radius: '16px' }),
            p: 1.1,
          }}
        >
          {groups.map((g, gi) => (
            <MDBox key={String(g.startMin)} sx={{ mb: gi === groups.length - 1 ? 0 : 1.0 }}>
              <TimeGroupHeader label={hm(g.startMin)} />

              <MDBox sx={{ display: 'grid', gap: 0.55 }}>
                {g.items.map((s) => {
                  const rowKey = sid(s);
                  const rowOpen = String(expandedId) === String(rowKey);

                  return (
                    <TodaySessionListRow
                      key={rowKey}
                      s={s}
                      open={rowOpen}
                      onToggle={() => setExpandedId(rowOpen ? null : rowKey)}
                    />
                  );
                })}
              </MDBox>

              {/* group divider (ProgramPanel-ish) */}
              {gi !== groups.length - 1 ? (
                <MDBox
                  sx={{
                    height: 1,
                    mt: 1.0,
                    background: isDark
                      ? `linear-gradient(90deg, transparent, ${alpha(
                          theme.palette.text.primary,
                          0.16
                        )}, transparent)`
                      : `linear-gradient(90deg, transparent, ${alpha(
                          theme.palette.text.primary,
                          0.1
                        )}, transparent)`,
                  }}
                />
              ) : null}
            </MDBox>
          ))}
        </MDBox>
      </MDBox>

      {/* Footer (keep) */}
      <MDBox
        sx={{
          px: isMobile ? 1.0 : 1.2,
          py: 'calc(env(safe-area-inset-bottom) + 10px)',
          display: 'flex',
          justifyContent: 'flex-end',
          background: isDark
            ? `linear-gradient(180deg, transparent, ${alpha(theme.palette.background.paper, 0.7)})`
            : `linear-gradient(180deg, transparent, ${alpha('#fff', 0.8)})`,
          backdropFilter: 'blur(10px)',
        }}
      >
        <Chip
          size="small"
          label="Not/Borç varsa açılır"
          sx={{
            height: 24,
            borderRadius: '999px',
            border: 'none',
            bgcolor: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.06),
            '& .MuiChip-label': { px: 1.0, fontSize: 11.5, fontWeight: 900 },
          }}
        />
      </MDBox>
    </Dialog>
  );
}

TodaySessionsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  todayISO: PropTypes.string,
  sessions: PropTypes.array,
  trainers: PropTypes.array,
};
